import React, { useState, useEffect, useRef, useCallback } from 'react';
import html2pdf from 'html2pdf.js';
import styled from 'styled-components';
import { Footer } from '../Footer';
import ApiKeyDebug from './ApiKeyDebug';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Arial', sans-serif;
`;

const Header = styled.h1`
  color: #1a73e8;
  text-align: center;
  margin-bottom: 2rem;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background-color: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #202124;
  margin-bottom: 0.5rem;
  display: block;
`;

const FileInput = styled.div`
  position: relative;
  overflow: hidden;
  display: inline-block;
  cursor: pointer;
`;

const HiddenInput = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  cursor: pointer;
`;

const FileButton = styled.button`
  background-color: #1a73e8;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #155ab9;
  }
`;

const FileName = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #5f6368;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }
`;

const Button = styled.button`
  background-color: #1a73e8;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  align-self: flex-start;
  
  &:hover {
    background-color: #155ab9;
  }
  
  &:disabled {
    background-color: #a8c7fa;
    cursor: not-allowed;
  }
`;

const TestButton = styled(Button)`
  margin-left: 1rem;
  background-color: #34a853;
  
  &:hover {
    background-color: #2e7d32;
  }
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const PreviewContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PDFPreview = styled.div`
  flex: 1;
  border: 1px solid #dadce0;
  border-radius: 8px;
  padding: 1rem;
  background-color: white;
  overflow: auto;
  max-height: 800px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

const ResultPreview = styled.div`
  flex: 1;
  border: 1px solid #dadce0;
  border-radius: 8px;
  padding: 1rem;
  background-color: white;
  overflow: auto;
  max-height: 800px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 50px;
  height: 50px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #1a73e8;
  animation: spin 1s ease-in-out infinite;
  margin: 2rem auto;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: #5f6368;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #d93025;
  background-color: #fce8e6;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  color: #137333;
  background-color: #e6f4ea;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const DebugSection = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const DebugLink = styled.a`
  color: #1a73e8;
  text-decoration: underline;
  cursor: pointer;
`;

const ConsoleLog = styled.pre`
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
  max-height: 200px;
  overflow: auto;
  font-size: 0.9rem;
  margin-top: 1rem;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const ResumeEditor = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [jobDescription, setJobDescription] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedResume, setGeneratedResume] = useState(null);
  const [fileName, setFileName] = useState('');
  const [apiKeyError, setApiKeyError] = useState(false);
  const [consoleMessages, setConsoleMessages] = useState([]);
  const [showConsole, setShowConsole] = useState(false);
  const resumeRef = useRef(null);
  
  // New state for custom improvements
  const [customPrompt, setCustomPrompt] = useState('');
  const [improvedResume, setImprovedResume] = useState(null);
  const improvedResumeRef = useRef(null);
  
  // Helper function to load scripts - exactly like pdf-test.html
  const loadScript = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);
  
  // Custom logging that displays in the UI for debugging
  const log = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const formattedMessage = `[${timestamp}] ${message}`;
    
    setConsoleMessages(prev => [...prev, { type, message: formattedMessage }]);
    
    if (type === 'error') {
      console.error(message);
    } else if (type === 'warn') {
      console.warn(message);
    } else {
      console.log(message);
    }
  }, []);
  
  // Configure PDF.js worker with fallbacks - exactly like pdf-test.html
  const configurePdfWorker = useCallback(async () => {
    log('Configuring PDF.js worker');
    
    try {
      // Load the PDF.js library with script tags if not already loaded
      if (!window.pdfjsLib || !window.pdfjsLib.getDocument) {
        log('Loading PDF.js from CDN');
        await loadScript('https://cdn.jsdelivr.net/npm/pdfjs-dist@3.10.111/build/pdf.min.js');
      }
      
      // Try multiple worker sources - exactly like in pdf-test.html
      const workerSources = [
        'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.10.111/build/pdf.worker.min.js',
        'https://unpkg.com/pdfjs-dist@3.10.111/build/pdf.worker.min.js',
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.10.111/pdf.worker.min.js'
      ];
      
      // First check if worker is already configured
      if (window.pdfjsWorkerSrc) {
        log(`Using preconfigured worker: ${window.pdfjsWorkerSrc}`);
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = window.pdfjsWorkerSrc;
        return true;
      }
      
      // Try each worker source until one works
      for (const workerSrc of workerSources) {
        try {
          log(`Trying worker from: ${workerSrc}`);
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
          
          // Test if the worker is loading correctly
          const testPdf = await fetch('/pdftest.pdf')
            .then(response => {
              if (!response.ok) {
                throw new Error('Test PDF not available');
              }
              return response.arrayBuffer();
            })
            .catch(error => {
              log(`Test PDF not available: ${error.message}`, 'warn');
              return null;
            });
          
          if (testPdf) {
            await window.pdfjsLib.getDocument({data: new Uint8Array(testPdf)}).promise;
            log(`Worker from ${workerSrc} loaded successfully!`);
            return true;
          }
          
          // If no test PDF available, just assume this worker is fine
          log(`No test PDF available, assuming worker ${workerSrc} is working`);
          return true;
        } catch (error) {
          log(`Worker from ${workerSrc} failed: ${error.message}`, 'warn');
        }
      }
      
      log('All worker sources failed', 'error');
      return false;
    } catch (error) {
      log(`Error configuring PDF.js worker: ${error.message}`, 'error');
      return false;
    }
  }, [log, loadScript]);
  
  // Check API endpoint availability on component mount
  useEffect(() => {
    // Clear previous state
    setConsoleMessages([]);
    
    // Initialize PDF.js
    (async function() {
      try {
        log('Initializing PDF.js');
        await configurePdfWorker();
      } catch (error) {
        log(`Failed to initialize PDF.js: ${error.message}`, 'error');
      }
    })();
    
    // Try to check if the API endpoint is available
    const checkApiEndpoint = async () => {
      try {
        const apiUrl = new URL('/api/process-resume.php', window.location.origin).href;
        log(`Checking API endpoint: ${apiUrl}`);
        
        const response = await fetch(apiUrl, {
          method: 'OPTIONS'
        });
        
        if (response.ok) {
          log('Resume API endpoint is available');
          setApiKeyError(false);
        } else {
          log(`Resume API endpoint returned an error: ${response.status}`, 'warn');
          setApiKeyError(true);
          setError('Resume tailoring API is not available. Please check server configuration.');
        }
      } catch (error) {
        log(`Error checking API endpoint: ${error.message}`, 'error');
        setApiKeyError(true);
        setError('Could not connect to the resume tailoring API. The resume editor functionality may not work.');
      }
    };
    
    checkApiEndpoint();
  }, [configurePdfWorker]);
  
  // Handle file upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setFileName(file.name);
      setError(null);
      setSuccess(null);
      log(`Selected file: ${file.name} (${formatFileSize(file.size)})`);
    } else {
      setPdfFile(null);
      setFileName('');
      setError('Please upload a valid PDF file.');
      log('Invalid file selected - must be a PDF', 'error');
    }
  };
  
  // Format file size - exactly like pdf-test.html
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  
  // Extract text from PDF - exactly like pdf-test.html
  const extractTextFromPDF = async () => {
    if (!pdfFile) {
      setError('Please select a PDF file first');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setExtractedText('');
    log('Starting PDF text extraction');
    
    try {
      // Ensure PDF.js is initialized
      if (!window.pdfjsLib || !window.pdfjsLib.getDocument) {
        log('PDF.js not initialized, attempting to initialize now');
        const initialized = await configurePdfWorker();
        if (!initialized) {
          throw new Error('Failed to initialize PDF.js');
        }
      }
      
      // Read the file
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = function() {
          resolve(new Uint8Array(this.result));
        };
        
        reader.onerror = function() {
          log(`FileReader error: ${this.error?.message || 'Unknown error'}`, 'error');
          reject(new Error(`Failed to read PDF file: ${this.error?.message || 'Unknown error'}`));
        };
        
        reader.readAsArrayBuffer(pdfFile);
      });
      
      log(`PDF loaded, size: ${fileData.length} bytes`);
      
      // Load the PDF document - exactly like pdf-test.html
      const pdf = await window.pdfjsLib.getDocument({
        data: fileData,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.10.111/cmaps/',
        cMapPacked: true
      }).promise;
      
      const numPages = pdf.numPages;
      log(`PDF loaded successfully with ${numPages} pages`);
      
      // Extract text from each page - exactly like pdf-test.html
      let extractedText = '';
      
      for (let i = 1; i <= numPages; i++) {
        try {
          log(`Processing page ${i}/${numPages}`);
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          extractedText += pageText + '\n\n';
        } catch (pageError) {
          log(`Error extracting text from page ${i}: ${pageError.message}`, 'error');
          // Continue with other pages despite error
        }
      }
      
      if (extractedText.trim()) {
        log('Text extraction complete');
        setExtractedText(extractedText);
        setSuccess('PDF text extracted successfully! You can now provide a job description and generate a tailored resume.');
        return extractedText;
      } else {
        throw new Error('No text could be extracted from the PDF');
      }
    } catch (error) {
      log(`Error during text extraction: ${error.message}`, 'error');
      setError(`Failed to extract text from PDF: ${error.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Process resume with OpenAI
  const processResume = async () => {
    // Extract text if it hasn't been done yet
    let textToProcess = extractedText;
    
    if (!textToProcess) {
      log('No text extracted yet, attempting extraction now');
      textToProcess = await extractTextFromPDF();
      if (!textToProcess) return; // Extraction failed
    }
    
    if (!jobDescription) {
      setError('Please provide a job description.');
      log('Job description is missing', 'error');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Use the server-side proxy for OpenAI API calls
      const apiUrl = new URL('/api/process-resume.php', window.location.origin).href;
      
      log(`Sending request to OpenAI via API proxy: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extractedText: textToProcess,
          jobDescription: jobDescription
        })
      });
      
      // Check if the response is ok
      if (!response.ok) {
        let errorMessage = `API error: ${response.status}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse JSON, just use the status text
        }
        
        throw new Error(errorMessage);
      }
      
      // Parse the response
      const data = await response.json();
      
      if (!data.result) {
        throw new Error('Invalid response from server');
      }
      
      // Set the generated resume
      log('Received tailored resume from OpenAI');
      setGeneratedResume(data.result);
      setSuccess('Resume tailored successfully! You can now download it as a PDF.');
    } catch (error) {
      log(`Error generating resume: ${error.message}`, 'error');
      setError(`Failed to generate tailored resume: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Process resume with custom improvements
  const processCustomImprovement = async () => {
    // Extract text if it hasn't been done yet
    let textToProcess = extractedText;
    
    if (!textToProcess) {
      log('No text extracted yet, attempting extraction now');
      textToProcess = await extractTextFromPDF();
      if (!textToProcess) return; // Extraction failed
    }
    
    if (!customPrompt.trim()) {
      setError('Please provide improvement instructions.');
      log('Custom improvement prompt is missing', 'error');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Use the server-side proxy for OpenAI API calls
      const apiUrl = new URL('/api/process-resume.php', window.location.origin).href;
      
      log(`Sending custom improvement request to OpenAI via API proxy: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extractedText: textToProcess,
          customPrompt: customPrompt
        })
      });
      
      // Check if the response is ok
      if (!response.ok) {
        let errorMessage = `API error: ${response.status}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse JSON, just use the status text
        }
        
        throw new Error(errorMessage);
      }
      
      // Parse the response
      const data = await response.json();
      
      if (!data.result) {
        throw new Error('Invalid response from server');
      }
      
      // Set the improved resume
      log('Received improved resume from OpenAI');
      setImprovedResume(data.result);
      setSuccess('Resume improved successfully! You can now download it as a PDF.');
    } catch (error) {
      log(`Error improving resume: ${error.message}`, 'error');
      setError(`Failed to improve resume: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Test PDF.js worker
  const testPdfWorker = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    log('Testing PDF.js worker...');
    
    try {
      // Try to initialize and test PDF.js
      const initialized = await configurePdfWorker();
      
      if (!initialized) {
        throw new Error('Failed to initialize PDF.js worker');
      }
      
      // Try to load our test PDF
      try {
        const response = await fetch('/pdftest.pdf');
        if (!response.ok) {
          throw new Error(`Failed to fetch test PDF: ${response.status}`);
        }
        
        const testPdfBuffer = await response.arrayBuffer();
        log(`Test PDF loaded, size: ${testPdfBuffer.byteLength} bytes`);
        
        // Try to parse it
        const pdf = await window.pdfjsLib.getDocument({
          data: new Uint8Array(testPdfBuffer),
          cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.10.111/cmaps/',
          cMapPacked: true
        }).promise;
        
        log(`Successfully loaded test PDF with ${pdf.numPages} pages`);
        
        // Extract some text to fully test
        const page = await pdf.getPage(1);
        await page.getTextContent(); // Just test that it works, don't need to store the result
        
        log('Successfully extracted text from test PDF');
        setSuccess('PDF.js is working correctly! You can now upload your resume.');
      } catch (testError) {
        log(`Error testing PDF.js with test file: ${testError.message}`, 'error');
        throw new Error(`PDF.js worker test failed: ${testError.message}`);
      }
    } catch (error) {
      log(`PDF.js worker test failed: ${error.message}`, 'error');
      setError(`PDF.js test failed: ${error.message}. Try using the standalone test tool at /pdf-test.html`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate PDF from the tailored resume
  const generatePDF = () => {
    if (!generatedResume) return;
    
    const resumeElement = resumeRef.current;
    
    const options = {
      margin: 1,
      filename: 'tailored-resume.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'cm', format: 'a4', orientation: 'portrait' }
    };
    
    log('Generating PDF from tailored resume');
    html2pdf().from(resumeElement).set(options).save();
  };
  
  // Generate PDF from the improved resume
  const generateImprovedPDF = () => {
    if (!improvedResume) return;
    
    const resumeElement = improvedResumeRef.current;
    
    const options = {
      margin: 1,
      filename: 'improved-resume.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'cm', format: 'a4', orientation: 'portrait' }
    };
    
    log('Generating PDF from improved resume');
    html2pdf().from(resumeElement).set(options).save();
  };
  
  return (
    <>
      <Container>
        <Header>Resume Tailoring Tool</Header>
        
        {error && (
          <ErrorMessage>
            {error}
            {error.includes('PDF') && (
              <DebugSection>
                <p><strong>PDF Troubleshooting:</strong></p>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>Try the "Test PDF.js" button to check if PDF.js is working properly</li>
                  <li>Use our <DebugLink href="/pdf-test.html" target="_blank">standalone PDF test tool</DebugLink> to diagnose issues</li>
                  <li>Try using Chrome or Edge for better PDF compatibility</li>
                  <li>Check if your PDF is standard-compliant (not password-protected or corrupted)</li>
                </ul>
              </DebugSection>
            )}
            {apiKeyError && (
              <DebugSection>
                <p><strong>OpenAI API Troubleshooting:</strong></p>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>Ensure the server-side config.php file contains a valid OpenAI API key</li>
                  <li>Verify your OpenAI API key has sufficient credits</li>
                  <li>Check that the API directory and files have proper permissions</li>
                </ul>
              </DebugSection>
            )}
          </ErrorMessage>
        )}
        
        {success && (
          <SuccessMessage>
            {success}
          </SuccessMessage>
        )}
        
        <InputSection>
          <div>
            <Label>Upload Your Resume (PDF)</Label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileInput>
                <FileButton>Choose File</FileButton>
                <HiddenInput 
                  type="file" 
                  accept=".pdf" 
                  onChange={handleFileChange}
                />
                {fileName && <FileName>{fileName}</FileName>}
              </FileInput>
              <TestButton 
                onClick={testPdfWorker}
                disabled={isLoading}
              >
                Test PDF.js
              </TestButton>
            </div>
          </div>
          
          <div>
            <Label>Paste Job Description</Label>
            <TextArea 
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job description here..."
            />
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button 
              onClick={extractTextFromPDF}
              disabled={isLoading || !pdfFile}
            >
              Extract Text
            </Button>
            
            <Button 
              onClick={processResume}
              disabled={isLoading || !jobDescription || apiKeyError}
            >
              Generate Tailored Resume
            </Button>
            
            <Button 
              onClick={() => setShowConsole(!showConsole)}
              style={{ marginLeft: 'auto', backgroundColor: '#5f6368' }}
            >
              {showConsole ? 'Hide Debug Log' : 'Show Debug Log'}
            </Button>
          </div>
          
          <ConsoleLog visible={showConsole}>
            {consoleMessages.map((msg, index) => (
              <div key={index} style={{ 
                color: msg.type === 'error' ? '#d93025' : 
                       msg.type === 'warn' ? '#e37400' : 
                       '#1a73e8' 
              }}>
                {msg.message}
              </div>
            ))}
          </ConsoleLog>
          
          {apiKeyError && <ApiKeyDebug />}
        </InputSection>

        {/* Long Resume Section for Custom Improvements */}
        <InputSection>
          <h2 style={{ color: '#1a73e8', marginBottom: '1rem' }}>Long Resume Section - Custom Improvements</h2>
          <p style={{ color: '#5f6368', marginBottom: '1.5rem' }}>
            Use this section to make specific improvements to your resume with custom instructions. 
            You can ask for enhancements like adding more detail, improving language, restructuring sections, 
            adding new sections, or any other specific changes you'd like to make.
          </p>
          
          <div>
            <Label>Custom Improvement Instructions</Label>
            <TextArea 
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Describe the specific improvements you'd like to make to your resume. For example: 'Add more technical details to my work experience', 'Improve the language to sound more professional', 'Add a projects section', 'Emphasize leadership experience', etc."
              style={{ minHeight: '120px' }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button 
              onClick={processCustomImprovement}
              disabled={isLoading || !customPrompt.trim() || apiKeyError}
            >
              Improve Resume
            </Button>
          </div>
        </InputSection>

        {isLoading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>
              {extractedText ? 'Generating tailored resume...' : 'Processing your resume...'}
              <br />This may take a minute...
            </LoadingText>
          </LoadingContainer>
        ) : (
          <PreviewSection>
            {pdfFile && extractedText && (
              <>
                <h2>Resume Preview</h2>
                <PreviewContainer>
                  <PDFPreview>
                    <h3>Original Resume Text</h3>
                    <pre style={{ 
                      whiteSpace: 'pre-wrap', 
                      fontFamily: 'inherit',
                      fontSize: '0.9rem'
                    }}>
                      {extractedText}
                    </pre>
                  </PDFPreview>
                  
                  {generatedResume && (
                    <ResultPreview>
                      <h3>Tailored Resume</h3>
                      <div ref={resumeRef} dangerouslySetInnerHTML={{ __html: generatedResume }} />
                      <Button 
                        onClick={generatePDF}
                        style={{ marginTop: '1rem' }}
                      >
                        Download as PDF
                      </Button>
                    </ResultPreview>
                  )}
                </PreviewContainer>
              </>
            )}
          </PreviewSection>
        )}
        
        {/* Improved Resume Preview Section */}
        {improvedResume && (
          <PreviewSection>
            <h2>Improved Resume Preview</h2>
            <ResultPreview>
              <h3>Custom Improved Resume</h3>
              <div ref={improvedResumeRef} dangerouslySetInnerHTML={{ __html: improvedResume }} />
              <Button 
                onClick={generateImprovedPDF}
                style={{ marginTop: '1rem' }}
              >
                Download Improved Resume as PDF
              </Button>
            </ResultPreview>
          </PreviewSection>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default ResumeEditor; 